/**
 * A voice note: play and pause, the waveform with the part already heard in the brand colour (owner
 * decision on the canon's Main board), the length — or the position while playing — and the speed.
 *
 * One `<audio>` per bubble, created on the first press so a long thread does not open forty media
 * elements. Only one note plays at a time: starting one pauses whichever was playing. The sender's
 * own note plays from the file they recorded while the server still processes it.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { mediaStatusOf, type ChatMessage } from '../model/chatMessage';
import { barsOf, durationLabel, nextVoiceSpeed, type VoiceSpeed } from '../model/voicePeaks';

import { PauseGlyph, PlayGlyph } from './ChatGlyphs';
import { useSignedUrl } from '../lib/useSignedUrl';
import styles from './chat.module.css';

const BAR_COUNT = 32;

let playing: HTMLAudioElement | null = null;

/** Set outside the component: the element is a ref's value, which a render may not write to. */
function applyRate(element: HTMLAudioElement, rate: number): void {
  element.playbackRate = rate;
}

function play(element: HTMLAudioElement, onRefused: () => void): void {
  if (playing !== null && playing !== element) playing.pause();
  playing = element;
  element.play().catch(onRefused);
}

export function VoiceBubble({ message }: { readonly message: ChatMessage }) {
  const { t } = useTranslation();
  const voice = message.wire.voice;
  const signed = useSignedUrl(voice?.mediaId, voice?.audioUrl, 'primary');
  const source = message.previewUrl ?? (mediaStatusOf(message) === 'Ready' ? signed.url : null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [speed, setSpeed] = useState<VoiceSpeed>(1);
  const duration = Math.max(0, voice?.durationSeconds ?? 0);
  const bars = barsOf(voice?.waveform ?? [], BAR_COUNT);
  const heard = duration > 0 ? position / duration : 0;

  useEffect(
    () => () => {
      const element = audio.current;
      if (element === null) return;
      element.pause();
      if (playing === element) playing = null;
    },
    [],
  );

  const toggle = () => {
    if (source === null) return;
    let element = audio.current;
    if (element === null || element.src !== source) {
      element?.pause();
      element = new Audio(source);
      element.preload = 'auto';
      element.addEventListener('timeupdate', () => {
        setPosition(audio.current?.currentTime ?? 0);
      });
      element.addEventListener('ended', () => {
        setPlaying(false);
        setPosition(0);
      });
      element.addEventListener('pause', () => {
        setPlaying(false);
      });
      element.addEventListener('play', () => {
        setPlaying(true);
      });
      element.addEventListener('error', () => {
        setPlaying(false);
        if (message.previewUrl === null) signed.onError();
      });
      audio.current = element;
    }
    applyRate(element, speed);
    if (element.paused) {
      play(element, () => {
        setPlaying(false);
      });
    } else {
      element.pause();
    }
  };

  const cycleSpeed = () => {
    const next = nextVoiceSpeed(speed);
    setSpeed(next);
    if (audio.current !== null) applyRate(audio.current, next);
  };

  const processing = mediaStatusOf(message) === 'Processing' && message.previewUrl === null;

  return (
    <div className={styles.voice} aria-label={t('chat.window.voiceA11y')} role="group">
      <button
        className={styles.voicePlay}
        type="button"
        onClick={toggle}
        disabled={source === null}
        aria-label={isPlaying ? t('chat.voice.pause') : t('chat.voice.play')}
      >
        {isPlaying ? <PauseGlyph /> : <PlayGlyph size={16} />}
      </button>
      <span className={styles.voiceBars} aria-hidden="true">
        {bars.map((value, index) => (
          <i
            // The bars are a fixed row; their index is their identity.
            key={index}
            className={index / bars.length < heard ? styles.voiceBarOn : undefined}
            style={{ height: `${String(4 + value * 0.78)}px` }}
          />
        ))}
      </span>
      <span className={`${styles.voiceDuration} ${styles.num}`}>
        {processing ? t('chat.window.videoProcessing') : durationLabel(isPlaying || position > 0 ? position : duration)}
      </span>
      <button
        className={`${styles.voiceSpeed} ${styles.num}`}
        type="button"
        onClick={cycleSpeed}
        aria-label={t('chat.voice.speed', { rate: speed })}
      >
        {speed}×
      </button>
    </div>
  );
}
